import { useEffect, useState } from 'react';
import { Package, Plus, Shield, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, api } from '@/App';
import AdminLayout from '@/components/AdminLayout';

export default function AdminSettings() {
  const { user } = useAuth();
  const [reviewUrl, setReviewUrl] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [quoteForm, setQuoteForm] = useState({ name: '', quote: '', detail: '' });
  const [twilioStatus, setTwilioStatus] = useState(null);

  const loadQuotes = () => api.get('/settings/testimonials').then((r) => setQuotes(r.data.items || [])).catch(() => {});
  const loadTwilio = () => api.get('/settings/twilio-status').then((r) => setTwilioStatus(r.data)).catch(() => {});

  useEffect(() => {
    if (user?.role !== 'admin') return;
    api.get('/settings/review').then((r) => setReviewUrl(r.data.url || '')).catch(() => {});
    loadQuotes();
    loadTwilio();
  }, [user]);

  const saveReviewUrl = async () => {
    try {
      await api.put('/settings/review', { url: reviewUrl.trim() });
      toast.success(reviewUrl.trim() ? 'Review button is live at gslab.engineer/review.' : 'Review link cleared.');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save the link.'); }
  };

  const addQuote = async (e) => {
    e.preventDefault();
    try {
      await api.post('/settings/testimonials', quoteForm);
      toast.success('Testimonial published to the homepage.');
      setQuoteForm({ name: '', quote: '', detail: '' });
      loadQuotes();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not add the testimonial.'); }
  };

  const removeQuote = async (id) => {
    try {
      await api.delete(`/settings/testimonials/${id}`);
      setQuotes((q) => q.filter((x) => x.id !== id));
    } catch { toast.error('Could not remove it.'); }
  };

  const toggleTwofa = async (enabled) => {
    try {
      await api.put('/settings/twofa', { enabled });
      toast.success(enabled ? 'SMS two-factor is ON for new registrations.' : 'SMS two-factor is off.');
      loadTwilio();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not change 2FA.'); }
  };

  const dismissTwilioReminder = async () => {
    try {
      await api.put('/settings/twilio-reminder', { active: false });
      toast.success('Twilio reminder dismissed.');
      loadTwilio();
    } catch { toast.error('Could not dismiss the reminder.'); }
  };

  const inputCls = 'w-full bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors placeholder:text-slate-600';

  return (
    <AdminLayout title="Site & Twilio Settings" subtitle="Google review link, homepage testimonials and SMS setup." testId="admin-settings-page">
      <div className="space-y-10">
        <div>
          <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
            <Package size={14} /> Google Review Link
          </h2>
          <div className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6">
            <p className="text-xs text-slate-500 mb-3">Paste your Google Business Profile review link — the button at gslab.engineer/review goes live instantly.</p>
            <div className="flex gap-3">
              <input data-testid="review-url-input" type="url" placeholder="https://g.page/r/…/review" value={reviewUrl}
                onChange={(e) => setReviewUrl(e.target.value)} className={`flex-1 ${inputCls}`} />
              <button data-testid="review-url-save-button" onClick={saveReviewUrl}
                className="text-sm bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-6 transition-shadow hover:shadow-[0_0_20px_rgba(212,175,55,0.35)]">
                Save
              </button>
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
            <Shield size={14} /> Twilio Setup
          </h2>
          <div className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6" data-testid="twilio-setup-section">
            {!twilioStatus ? (
              <p className="text-xs text-slate-500">Loading…</p>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`font-mono-tech text-[10px] tracking-widest border rounded-full px-3 py-1 ${twilioStatus.configured ? 'text-emerald-300 border-emerald-500/40' : 'text-slate-400 border-slate-600/50'}`}>
                    {twilioStatus.configured ? 'KEYS CONFIGURED' : 'KEYS MISSING'}
                  </span>
                  <span className={`font-mono-tech text-[10px] tracking-widest border rounded-full px-3 py-1 ${twilioStatus.twofa ? 'text-emerald-300 border-emerald-500/40' : 'text-slate-400 border-slate-600/50'}`}>
                    2FA {twilioStatus.twofa ? 'ON' : 'OFF'}
                  </span>
                  {twilioStatus.reminder_active && !twilioStatus.configured && (
                    <span className="font-mono-tech text-[10px] tracking-widest border rounded-full px-3 py-1 text-amber-300 border-amber-500/40">WEEKLY EIN REMINDER ON</span>
                  )}
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Once your GSLAB LLC EIN arrives: create the Twilio compliance profile, get a phone number and a Verify Service SID,
                  then save the four keys (Account SID, Auth Token, phone number, Verify SID) — SMS alerts and two-factor activate automatically.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button data-testid="twofa-toggle-button" onClick={() => toggleTwofa(!twilioStatus.twofa)}
                    className="text-xs border border-amber-500/40 text-amber-300 rounded-full px-5 py-2 hover:bg-amber-500/10 transition-colors">
                    {twilioStatus.twofa ? 'Turn 2FA off' : 'Turn 2FA on'}
                  </button>
                  {twilioStatus.reminder_active && !twilioStatus.configured && (
                    <button data-testid="dismiss-twilio-reminder-button" onClick={dismissTwilioReminder}
                      className="text-xs border border-slate-600 text-slate-300 rounded-full px-5 py-2 hover:border-slate-400 transition-colors">
                      Dismiss EIN reminder
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
            <Star size={14} /> Homepage Testimonials
          </h2>
          <div className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6">
            <p className="text-xs text-slate-500 mb-4">Real client quotes shown on the homepage review showcase. Empty = section stays hidden.</p>
            {quotes.length > 0 && (
              <div className="space-y-2 mb-5">
                {quotes.map((q) => (
                  <div key={q.id} data-testid="testimonial-row" className="flex items-start justify-between gap-3 border border-slate-800 rounded-lg bg-[#0B1528]/70 px-4 py-3">
                    <div>
                      <div className="text-sm text-slate-200">{q.name} <span className="text-slate-500 text-xs">{q.detail}</span></div>
                      <div className="text-xs text-slate-500 mt-1">“{q.quote.slice(0, 90)}{q.quote.length > 90 ? '…' : ''}”</div>
                    </div>
                    <button data-testid="testimonial-delete-button" onClick={() => removeQuote(q.id)} className="text-slate-500 hover:text-red-400 text-xs transition-colors">✕</button>
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={addQuote} className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="testimonial-name-input" required placeholder="Client name — e.g. Marta Alvarez" value={quoteForm.name}
                  onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })} className={inputCls} />
                <input data-testid="testimonial-detail-input" placeholder="Detail — e.g. Landscaping, Atlanta" value={quoteForm.detail}
                  onChange={(e) => setQuoteForm({ ...quoteForm, detail: e.target.value })} className={inputCls} />
              </div>
              <textarea data-testid="testimonial-quote-input" required rows={3} placeholder="Their quote, in their words…" value={quoteForm.quote}
                onChange={(e) => setQuoteForm({ ...quoteForm, quote: e.target.value })} className={inputCls} />
              <button data-testid="testimonial-add-button" type="submit"
                className="inline-flex items-center gap-2 border border-amber-500/40 text-amber-300 rounded-full px-6 py-2.5 text-sm hover:bg-amber-500/10 transition-colors">
                <Plus size={14} /> Publish testimonial
              </button>
            </form>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
